import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import cx from 'clsx';
import PropTypes from 'prop-types';
import {
  useBreakpoint,
  useMatchMedia,
} from '@nike/nike-design-system-components';
import { actions } from '@nike/ciclp-redux-app';
import {
  eventNames,
  analyticsProperties,
  clickActivityProperties,
} from '@nike/ciclp-redux-app/analytics/constants';
import styles from './autoplayCarousel.styl';
import { AutoplayCarouselSlide } from './autoplayCarouselSlide';
import { NavigationButtons } from './NavigationButtons';
import { ProgressPauseButton } from './ProgressPauseButton';
import { PaginationIndicators } from './PaginationIndicators';
import { gallerySlideShape, THEMES } from '../../shapes';
import { useGetSlideDelay } from './hooks';
import { useCarouselUtils } from './carouselUtils';
import { TRANSITION_DURATION } from './constants';

// Visually hidden styles for screen reader live region
const srOnlyStyles = {
  height: '1px',
  left: '-10000px',
  overflow: 'hidden',
  position: 'absolute',
  width: '1px',
};

export const AutoplayCarousel = ({
  slides = [],
  analyticsItemId = '',
  cardId = '',
  hashKey = '',
  translations = {},
}) => {
  const dispatch = useDispatch();
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentTransformIndex, setCurrentTransformIndex] = useState(1); // Start at 1 to account for duplicate
  const [liveRegionMessage, setLiveRegionMessage] = useState('');
  const [isVideoFullMode, setIsVideoFullMode] = useState(false); // Tracks when user is watching video with full controls
  const [transitionDirection, setTransitionDirection] = useState(null); // 'next' | 'prev' | null - for parallax effect

  const carouselRef = useRef(null);
  const progressCircle = useRef(null);
  const timerRef = useRef(null);
  const timeLeftRef = useRef(null);
  const prevSlideIndexRef = useRef(activeSlideIndex);
  const touchStateRef = useRef({
    startTime: null,
    startX: null,
    startY: null,
    wasAutoPlaying: null,
  });
  const transitionTimeoutRef = useRef(null);
  const getSlideDelay = useGetSlideDelay();

  const mobileMediaQuery = useBreakpoint(`lt`, `s`);
  const isMobile = useMatchMedia(mobileMediaQuery);
  const prefersReducedMotion = useMatchMedia(
    '(prefers-reduced-motion: reduce)',
  );

  const slidesFullControlsEnabled =
    slides?.some(
      slide =>
        slide?.controlOptions && Object.keys(slide.controlOptions).length > 0,
    ) ?? false;

  const colorTheme = slides[activeSlideIndex]?.colorTheme;

  // Initialize carousel utility functions
  const {
    pauseVideo,
    restartVideo,
    resumeVideo,
    stopAllVideos,
    stopInactiveVideos,
    isActiveSlideVideo,
    updateFocusableElements,
    goToSlide,
    goToNextSlide,
    goToPrevSlide,
    startAutoplay,
    stopAutoplay,
    handleTouchStart,
    handleTouchEnd,
    handleTouchCancel,
  } = useCarouselUtils({
    activeSlideIndex,
    carouselRef,
    currentTransformIndex,
    getSlideDelay,
    isAutoPlaying,
    isMobile,
    isTransitioning,
    prefersReducedMotion,
    progressCircle,
    setActiveSlideIndex,
    setCurrentTransformIndex,
    setIsTransitioning,
    setTransitionDirection,
    slides,
    slidesFullControlsEnabled,
    timeLeftRef,
    timerRef,
    touchStateRef,
    transitionTimeoutRef,
    translations,
  });

  // Event handlers
  const handlePrevClick = useCallback(() => {
    goToPrevSlide();
    dispatch(
      actions.analyticsActions.track({
        eventName: eventNames.AUTOPLAY_PREVIOUS_CLICK,
        [analyticsProperties.CLICK_ACTIVITY]:
          clickActivityProperties[eventNames.AUTOPLAY_PREVIOUS_CLICK],
      }),
    );
  }, [goToPrevSlide, dispatch]);

  const handleNextClick = useCallback(() => {
    goToNextSlide();
    dispatch(
      actions.analyticsActions.track({
        eventName: eventNames.AUTOPLAY_NEXT_CLICK,
        [analyticsProperties.CLICK_ACTIVITY]:
          clickActivityProperties[eventNames.AUTOPLAY_NEXT_CLICK],
      }),
    );
  }, [goToNextSlide, dispatch]);

  const handlePlayPauseClick = useCallback(() => {
    const newIsAutoPlaying = !isAutoPlaying;
    setIsAutoPlaying(newIsAutoPlaying);

    // Announce autoplay state change to screen readers
    setLiveRegionMessage(
      newIsAutoPlaying
        ? translations.autoplayResumeButtonLabel || 'Carousel autoplay resumed'
        : translations.autoplayPauseButtonLabel || 'Carousel autoplay paused',
    );

    // Handle video play/pause
    if (isActiveSlideVideo()) {
      if (newIsAutoPlaying) {
        resumeVideo();
      } else {
        pauseVideo();
      }
    }

    const eventName = newIsAutoPlaying
      ? eventNames.AUTOPLAY_PLAY
      : eventNames.AUTOPLAY_PAUSE;
    dispatch(
      actions.analyticsActions.track({
        eventName,
        [analyticsProperties.CLICK_ACTIVITY]:
          clickActivityProperties[eventName],
      }),
    );
  }, [
    isAutoPlaying,
    dispatch,
    isActiveSlideVideo,
    resumeVideo,
    pauseVideo,
    translations,
  ]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback(
    e => {
      // Prevent navigation when user is in video full mode
      if (isVideoFullMode) {
        return;
      }

      // Only handle keyboard events when carousel container or its controls have focus
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevClick();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNextClick();
      } else if (e.key === 'Home') {
        e.preventDefault();
        goToSlide(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        goToSlide(slides.length - 1);
      }
    },
    [
      handlePrevClick,
      handleNextClick,
      goToSlide,
      slides.length,
      isVideoFullMode,
    ],
  );

  // Handler for video overlay controls (WATCH button and back button)
  const handleAutoplayControl = useCallback(
    action => {
      if (action === 'pause') {
        setIsAutoPlaying(false);
        setIsVideoFullMode(true);
        stopAutoplay();
      } else if (action === 'resume') {
        setIsAutoPlaying(true);
        setIsVideoFullMode(false);
        // Restart the video from the beginning when exiting full mode
        if (isActiveSlideVideo()) {
          restartVideo();
        }
      }
    },
    [stopAutoplay, isActiveSlideVideo, restartVideo],
  );

  // Effects
  useEffect(() => {
    updateFocusableElements(activeSlideIndex);

    // Reset video full mode when slide changes
    // This ensures controls are restored if user navigates away from video slide
    if (isVideoFullMode) {
      setIsVideoFullMode(false);
    }

    // Announce slide change to screen readers
    const slideNumber = activeSlideIndex + 1;
    const totalSlides = slides.length;
    const message =
      translations.slideChanged
        ?.replace('{current}', slideNumber)
        ?.replace('{total}', totalSlides) ||
      `Slide ${slideNumber} of ${totalSlides}`;
    setLiveRegionMessage(message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlideIndex, updateFocusableElements, slides.length, translations]);

  // Video management effect - stop inactive videos and resume active video when slide changes
  useEffect(() => {
    // After transition completes, stop and reset videos on inactive slides
    if (!isTransitioning && slides?.length > 0) {
      stopInactiveVideos();

      const slideActuallyChanged =
        prevSlideIndexRef.current !== activeSlideIndex;

      // If we navigated to a video slide while autoplay is on, ensure video plays
      // This handles the case where video was paused via carousel pause button,
      // then autoplay was resumed, then user navigated back to the video slide
      if (slideActuallyChanged && isActiveSlideVideo() && isAutoPlaying) {
        resumeVideo();
      }

      // Track slide changes
      if (slideActuallyChanged) {
        prevSlideIndexRef.current = activeSlideIndex;
      }
    }
  }, [
    activeSlideIndex,
    isAutoPlaying,
    isActiveSlideVideo,
    isTransitioning,
    resumeVideo,
    slides?.length,
    stopInactiveVideos,
  ]);

  // Autoplay effect
  useEffect(() => {
    if (isAutoPlaying && !isTransitioning) {
      startAutoplay();
    } else {
      stopAutoplay();
    }

    return () => {
      stopAutoplay();
    };
  }, [
    isAutoPlaying,
    activeSlideIndex,
    isTransitioning,
    startAutoplay,
    stopAutoplay,
  ]);

  // Slides synchronization effect - reset state when slides change to prevent invalid indices
  useEffect(() => {
    const totalSlides = slides?.length ?? 0;
    if (totalSlides === 0) return;

    // Clamp activeSlideIndex to valid range if slides array shrinks
    if (activeSlideIndex >= totalSlides) {
      // Clear any pending transition timeout to prevent stale closure execution
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }

      stopAutoplay();
      setActiveSlideIndex(0);
      setCurrentTransformIndex(1); // Reset to start position (accounting for duplicate)
      setIsTransitioning(false); // Reset transition state
      stopAllVideos();
      prevSlideIndexRef.current = 0;

      // Reset progress to 0 to prevent startAutoplay from using stale progress values
      if (progressCircle.current) {
        progressCircle.current.style.setProperty('--progress', 0);
      }
    }
  }, [slides?.length, activeSlideIndex, stopAllVideos, stopAutoplay]);

  // Cleanup effect - stop ALL videos and clear pending timeouts when component unmounts
  useEffect(() => {
    return () => {
      stopAllVideos();
      // Clear any pending transition timeout to prevent state updates on unmounted component
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
    };
  }, [stopAllVideos]);

  // Component styles
  const carouselClasses = cx(styles.autoplayCarousel, {
    [styles.dark]: colorTheme === THEMES.DARK,
    [styles.transitioning]: isTransitioning,
  });

  return (
    <div
      className={carouselClasses}
      id={`carousel-${cardId}`}
      role="region"
      aria-label={translations.autoplayCarousel || 'Autoplay Carousel'}
      onKeyDown={handleKeyDown}
      onTouchStart={e => !isVideoFullMode && handleTouchStart(e, stopAutoplay)}
      onTouchEnd={e =>
        !isVideoFullMode &&
        handleTouchEnd(e, handleNextClick, handlePrevClick, startAutoplay)
      }
      onTouchCancel={() => !isVideoFullMode && handleTouchCancel(startAutoplay)}
    >
      <div
        className={styles.slidesContainer}
        ref={carouselRef}
        style={{
          transform: `translateX(-${currentTransformIndex * 100}%)`,
          transition:
            isTransitioning && !prefersReducedMotion
              ? `transform ${TRANSITION_DURATION}ms ease-in-out`
              : 'none',
        }}
      >
        {/* Render slides with duplicates for infinite scroll */}
        {slides &&
          slides.length > 0 && [
            // Last slide duplicate (for seamless backward scroll)
            ...slides.slice(-1).map(slide => {
              const actualIndex = slides.length - 1;
              const slideKey = slide?.id ?? `slide-${actualIndex}`;
              return (
                <div
                  key={`duplicate-last-${slideKey}`}
                  className={cx(styles.slide, {
                    [styles.parallaxNext]:
                      isTransitioning &&
                      transitionDirection === 'next' &&
                      currentTransformIndex === 0 &&
                      !prefersReducedMotion,
                    [styles.parallaxPrev]:
                      isTransitioning &&
                      transitionDirection === 'prev' &&
                      currentTransformIndex === 0 &&
                      !prefersReducedMotion,
                  })}
                  data-slide-delay={getSlideDelay(slide)}
                  aria-hidden="true"
                >
                  <AutoplayCarouselSlide
                    {...slide}
                    cardId={cardId}
                    index={actualIndex}
                    analyticsItemId={`${analyticsItemId}_${actualIndex + 1}`}
                    itemCount={slides.length}
                    collectionId={analyticsItemId}
                    hashKey={slide.hashKey || hashKey}
                    onAutoplayControl={handleAutoplayControl}
                  />
                </div>
              );
            }),
            // Original slides
            ...slides.map((slide, index) => {
              const slideDelay = getSlideDelay(slide);
              const isVideo = slide?.containerType?.toUpperCase() === 'VIDEO';
              const mediaType = isVideo ? 'video' : 'image';
              const slideTitle = slide?.titleProps?.text;
              const titleSuffix = slideTitle ? `: ${slideTitle}` : '';
              const slideKey = slide?.id ?? `slide-${index}`;
              const slideNumber = index + 1;
              const baseLabel =
                translations.autoplaySlideNumber
                  ?.replace('{slideNum}', slideNumber)
                  ?.replace('{slidesTotalNum}', slides.length) ||
                `Slide ${slideNumber} of ${slides.length}`;
              const slideLabel = `${baseLabel}, ${mediaType}${titleSuffix}`;
              return (
                <div
                  key={slideKey}
                  className={cx(styles.slide, {
                    [styles.parallaxNext]:
                      isTransitioning &&
                      transitionDirection === 'next' &&
                      index + 1 === currentTransformIndex &&
                      !prefersReducedMotion,
                    [styles.parallaxPrev]:
                      isTransitioning &&
                      transitionDirection === 'prev' &&
                      index + 1 === currentTransformIndex &&
                      !prefersReducedMotion,
                  })}
                  data-slide-delay={slideDelay}
                  role="group"
                  aria-label={slideLabel}
                  aria-hidden={index !== activeSlideIndex}
                >
                  <AutoplayCarouselSlide
                    {...slide}
                    cardId={cardId}
                    index={index}
                    analyticsItemId={`${analyticsItemId}_${index + 1}`}
                    itemCount={slides.length}
                    collectionId={analyticsItemId}
                    hashKey={slide.hashKey || hashKey}
                    onAutoplayControl={handleAutoplayControl}
                  />
                </div>
              );
            }),
            // First slide duplicate (for seamless forward scroll)
            ...slides.slice(0, 1).map(slide => {
              const slideKey = slide?.id ?? 'slide-0';
              return (
                <div
                  key={`duplicate-first-${slideKey}`}
                  className={cx(styles.slide, {
                    [styles.parallaxNext]:
                      isTransitioning &&
                      transitionDirection === 'next' &&
                      currentTransformIndex === slides.length + 1 &&
                      !prefersReducedMotion,
                    [styles.parallaxPrev]:
                      isTransitioning &&
                      transitionDirection === 'prev' &&
                      currentTransformIndex === slides.length + 1 &&
                      !prefersReducedMotion,
                  })}
                  data-slide-delay={getSlideDelay(slide)}
                  aria-hidden="true"
                >
                  <AutoplayCarouselSlide
                    {...slide}
                    cardId={cardId}
                    index={0}
                    analyticsItemId={`${analyticsItemId}_1`}
                    itemCount={slides.length}
                    collectionId={analyticsItemId}
                    hashKey={slide.hashKey || hashKey}
                    onAutoplayControl={handleAutoplayControl}
                  />
                </div>
              );
            }),
          ]}
      </div>

      {/* Hide controls when user is watching video with full controls */}
      {!isVideoFullMode && (
        <>
          <div className={styles.controls}>
            {/* Progress/Pause Button */}
            <ProgressPauseButton
              colorTheme={colorTheme}
              isAutoPlaying={isAutoPlaying}
              progressCircleRef={progressCircle}
              onPlayPauseClick={handlePlayPauseClick}
              translations={translations}
            />

            {/* Navigation Buttons */}
            {!isMobile && (
              <NavigationButtons
                colorTheme={colorTheme}
                onPrevClick={handlePrevClick}
                onNextClick={handleNextClick}
                translations={translations}
              />
            )}
          </div>

          {/* Pagination Indicators */}
          <PaginationIndicators
            slides={slides}
            activeSlideIndex={activeSlideIndex}
            onSlideClick={goToSlide}
            paginationLabel={`${translations.autoplayCarousel} ${translations.paginationTitle}`}
          />
        </>
      )}

      {/* Live region for screen reader announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        style={srOnlyStyles}
      >
        {liveRegionMessage}
      </div>
    </div>
  );
};

AutoplayCarousel.propTypes = {
  analyticsItemId: PropTypes.string,
  cardId: PropTypes.string,
  hashKey: PropTypes.string,
  slides: PropTypes.arrayOf(PropTypes.shape(gallerySlideShape)),
  translations: PropTypes.objectOf(PropTypes.string),
};
